import CopyLinkButton from '@/components/Discord/CopyLinkButton'
import { useState } from 'react'
import GuidelineTooltip from '../../../components/Discord/GuidelineTooltip'
import styles from '@/components/Discord/DiscordDocument.module.css'
import DiscordDocument from '@/components/Discord/DiscordDocument'

function DiscordViolentExtremismPolicyPage() {
  const [language, setLanguage] = useState<'en' | 'cs'>('cs')

  return (
    <DiscordDocument
      language={language}
      onLanguageChange={setLanguage}
      heading={
        <div className={styles.titleSection}>
          <h1 className={styles.title}>
            {language === 'cs' ? 'Politika násilného extremismu' : 'Violent Extremism Policy'}
          </h1>
        </div>
      }
      notice={
        <>
          <div className={styles.disclaimer}>
            <div className={styles.disclaimerCard} lang="cs">
              <h3>Důležité upozornění</h3>
              <p>
                Nejsme přidruženi ani spojeni s Discord Inc. Toto je pouze komunitní překlad
                oficiální stránky o politice násilného extremismu Discord dostupné na{' '}
                <a
                  href="https://discord.com/safety/violent-extremism-policy-explainer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://discord.com/safety/violent-extremism-policy-explainer
                </a>
                .
              </p>
              <p>
                Tento překlad nemusí být aktuální s oficiální politikou. Pokud si všimnete rozdílu
                mezi oficiální stránkou a touto stránkou, prosím, dejte nám vědět na našem Discord
                serveru.
              </p>
            </div>
            <div className={styles.disclaimerCard} lang="en">
              <h3>Important Notice</h3>
              <p>
                We are not affiliated with or associated with Discord Inc. This is only a community
                translation of the official Discord violent extremism policy available at{' '}
                <a
                  href="https://discord.com/safety/violent-extremism-policy-explainer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://discord.com/safety/violent-extremism-policy-explainer
                </a>
                .
              </p>
              <p>
                This translation may not be up to date with the official policy. If you notice a
                difference between the official page and this page, please let us know on our
                Discord server.
              </p>
            </div>
          </div>
        </>
      }
    >
      {/* Disclaimer section - always show both languages */}

      {language === 'cs' && (
        <details className={styles.quickSummary} id="quick-summary">
          <summary>
            <h2>Rychlé shrnutí politiky</h2>
          </summary>
          <CopyLinkButton
            sectionId={'quick-summary'}
            language={language}
            className={styles.copyBtn}
          />

          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <h3>Co je zakázáno</h3>
              <ul>
                <li>Organizování, propagace nebo podpora násilného extremismu</li>
                <li>Sdílení propagandy teroristických skupin</li>
                <li>Koordinace násilných činů</li>
                <li>Glorifikace násilí nebo pachatelů</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Příklady porušení</h3>
              <ul>
                <li>Nábor do extremistických skupin</li>
                <li>Fundraising pro teroristické organizace</li>
                <li>Doxxing nebo útoky jménem extremistů</li>
                <li>Sdílení konspiračních teorií podněcujících násilí</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Jak se chránit</h3>
              <ul>
                <li>Nesdílejte nenávistný obsah ani jako vtip</li>
                <li>Netvrďte, že jste členem extremistické skupiny</li>
                <li>Nahlaste podezřelý obsah</li>
                <li>Opusťte servery s tímto obsahem</li>
              </ul>
            </div>
          </div>
        </details>
      )}

      <div className={styles.article}>
        {language === 'cs' ? (
          <>
            <section id="violent-extremism" className={styles.sectionWrapper}>
              <CopyLinkButton
                sectionId={'violent-extremism'}
                language={language}
                className={styles.copyBtn}
              />
              <div className={styles.sectionContent}>
                <h2>
                  <strong>Násilný extremismus</strong>
                </h2>
                <p>
                  Na Discordu zaujímáme pevný postoj proti násilnému extremismu. Netolerujeme
                  přítomnost ani aktivity násilných extremistických organizací. Uživatelé nesmí
                  používat Discord k organizování, propagaci nebo podpoře násilných extremistických
                  organizací, aktivit nebo přesvědčení. Termín{' '}
                  <strong>násilná extremistická organizace</strong> popisuje skupiny, které
                  prosazují politickou, ideologickou nebo náboženskou agendu a tolerují, obhajují
                  nebo používají násilí k dosažení svých cílů.
                </p>
                <p>
                  Tato politika pokrývá širokou škálu aktérů, jako jsou teroristické skupiny,
                  násilné extremistické organizace a násilné nenávistné skupiny. Zahrnujeme také
                  neformální skupiny nebo sítě, stejně jako polovojenské a nestátní ozbrojené
                  skupiny, které jsou organizovány kolem extremistických přesvědčení podle této
                  politiky.
                </p>
                <p>
                  I když nejsou spojeni s násilnou extremistickou skupinou, uživatelé nesmí sdílet
                  nebo propagovat obsah spojený s těmito skupinami nebo obsah, který obhajuje
                  násilná extremistická přesvědčení na Discordu.
                </p>
                <p>
                  Jako jedna z oblastí zneužívání s nejvyšší mírou škod podléhá organizování
                  násilného extremismu také naší{' '}
                  <a
                    href="https://discord.com/safety/addressing-harmful-off-platform-behavior"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Politice chování mimo platformu
                  </a>
                  .
                </p>
              </div>
            </section>

            <section id="how-to-avoid" className={styles.sectionWrapper}>
              <CopyLinkButton
                sectionId={'how-to-avoid'}
                language={language}
                className={styles.copyBtn}
              />
              <div className={styles.sectionContent}>
                <h2>
                  <strong>Jak se vyhnout porušení této politiky</strong>
                </h2>
                <ul>
                  <li>
                    Nezveřejňujte ani nesdílejte nenávistný obsah nebo obsah, který oslavuje nebo
                    podporuje násilí — ani jako vtip
                  </li>
                  <li>
                    Nezveřejňujte obsah, pokud víte, že byl vytvořen teroristickou organizací nebo
                    nenávistnou skupinou nebo je s nimi spojen
                  </li>
                  <li>
                    Nepředstírejte ani netvrdíte ostatním, že jste členem teroristické nebo násilné
                    extremistické skupiny
                  </li>
                  <li>
                    Pokud se ocitnete na serveru, který sdílí tento typ obsahu,{' '}
                    <a
                      href="/discord/safety/reporting-abusive-behavior-to-discord"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      nahlaste to
                    </a>{' '}
                    a zvažte své odstranění z tohoto prostoru
                  </li>
                </ul>
                <p>
                  Nahlaste a opusťte servery věnované jakémukoli výše popsanému porušujícímu obsahu.
                  Discord může také podniknout kroky proti účtům, které jsou přítomny na
                  porušujících serverech.
                </p>
              </div>
            </section>

            <section id="how-to-report" className={styles.sectionWrapper}>
              <CopyLinkButton
                sectionId={'how-to-report'}
                language={language}
                className={styles.copyBtn}
              />
              <div className={styles.sectionContent}>
                <h2>
                  <strong>Jak nahlásit porušení této politiky</strong>
                </h2>
                <p>
                  Nahlašování porušení bezpečnosti je kriticky důležité pro udržení bezpečnosti vás
                  a širší komunity Discord. Všichni uživatelé Discordu mohou nahlásit porušení zásad
                  přímo v aplikaci podle pokynů{' '}
                  <a
                    href="/discord/safety/reporting-abusive-behavior-to-discord"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    zde
                  </a>
                  . Pro více informací o této politice se prosím podívejte na naše{' '}
                  <a href="/discord/guidelines">Pokyny komunity</a>{' '}
                  <GuidelineTooltip guidelineNumber="5" language={language}>
                    <a href="/discord/guidelines#guideline-5" className={styles.guidelineLink}>
                      #5
                    </a>
                  </GuidelineTooltip>
                  .
                </p>
              </div>
            </section>

            <section id="resources" className={styles.sectionWrapper}>
              <CopyLinkButton
                sectionId={'resources'}
                language={language}
                className={styles.copyBtn}
              />
              <div className={styles.sectionContent}>
                <h2>
                  <strong>Zdroje</strong>
                </h2>
                <p>
                  Pokud jste vy nebo jiný uživatel v nebezpečí, okamžitě kontaktujte orgány činné v
                  trestním řízení a dejte jim vědět, co se děje, bez ohledu na informace, které
                  můžete poskytnout.
                </p>
              </div>
            </section>
          </>
        ) : (
          <>
            <section id="violent-extremism" className={styles.sectionWrapper}>
              <CopyLinkButton
                sectionId={'violent-extremism'}
                language={language}
                className={styles.copyBtn}
              />
              <div className={styles.sectionContent}>
                <h2>
                  <strong>Violent Extremism</strong>
                </h2>
                <p>
                  At Discord, we take a firm stance against violent extremism. We do not tolerate
                  the presence or activities of violent extremist organizations. Users are not
                  allowed to use Discord to organize, promote, or support violent extremist
                  organizations, activities or beliefs. The term{' '}
                  <strong>violent extremist organization</strong> describes groups that promote a
                  political, ideological, or religious agenda and tolerate, advocate for, or use
                  violence to achieve their goals.
                </p>
                <p>
                  This policy covers a wide range of actors, such as terrorist groups, violent
                  extremist organizations, and violent hate groups. We also include informal groups
                  or networks, as well as paramilitary and non-state armed groups, that are
                  organized around extremist beliefs under this policy.
                </p>
                <p>
                  Even if not associated with a violent extremist group, users may not share or
                  promote content associated with these groups or content that advocates for violent
                  extremist beliefs on Discord.
                </p>
                <p>
                  As one of the highest-harm areas of abuse, violent extremist organizing is also
                  subject to our{' '}
                  <a
                    href="https://discord.com/safety/addressing-harmful-off-platform-behavior"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Off-Platform Behaviors Policy
                  </a>
                  .
                </p>
              </div>
            </section>

            <section id="how-to-avoid" className={styles.sectionWrapper}>
              <CopyLinkButton
                sectionId={'how-to-avoid'}
                language={language}
                className={styles.copyBtn}
              />
              <div className={styles.sectionContent}>
                <h2>
                  <strong>How to Avoid Violating This Policy</strong>
                </h2>
                <ul>
                  <li>
                    Don't post or share hateful content or content that glorifies or encourages
                    violence — even as a joke
                  </li>
                  <li>
                    Don't post content if you know it was created by or is associated with a
                    terrorist organization or hate group
                  </li>
                  <li>
                    Don't pretend or claim to be a member of a terrorist or violent extremist group
                    to others
                  </li>
                  <li>
                    If you find yourself in a server that is sharing this type of content,{' '}
                    <a
                      href="/discord/safety/reporting-abusive-behavior-to-discord"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      report
                    </a>{' '}
                    it and consider removing yourself from the space
                  </li>
                </ul>
                <p>
                  Please report and leave servers dedicated to any of the violative content
                  described above. Discord may also take action against accounts that are present in
                  violative servers.
                </p>
              </div>
            </section>

            <section id="how-to-report" className={styles.sectionWrapper}>
              <CopyLinkButton
                sectionId={'how-to-report'}
                language={language}
                className={styles.copyBtn}
              />
              <div className={styles.sectionContent}>
                <h2>
                  <strong>How to Report Violations of This Policy</strong>
                </h2>
                <p>
                  Reporting safety violations is critically important to keeping you and the broader
                  Discord community safe. All Discord users can report policy violations right in
                  the app by following the instructions{' '}
                  <a
                    href="/discord/safety/reporting-abusive-behavior-to-discord"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                  . For more information on this policy, please reference our{' '}
                  <a
                    href="https://discord.com/guidelines"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Community Guidelines
                  </a>{' '}
                  <GuidelineTooltip guidelineNumber="5" language={language}>
                    <a href="/discord/guidelines#guideline-5" className={styles.guidelineLink}>
                      #5
                    </a>
                  </GuidelineTooltip>
                  .
                </p>
              </div>
            </section>

            <section id="resources" className={styles.sectionWrapper}>
              <CopyLinkButton
                sectionId={'resources'}
                language={language}
                className={styles.copyBtn}
              />
              <div className={styles.sectionContent}>
                <h2>
                  <strong>Resources</strong>
                </h2>
                <p>
                  If you or another user is in danger, please contact law enforcement right away and
                  let them know what's going on, regardless of the information you're able to
                  provide.
                </p>
              </div>
            </section>
          </>
        )}
      </div>

      <div className={styles.tags}>
        <div className={styles.tagsLabel}>Tags</div>
        <div className={styles.tagsList}>
          <div className={styles.tag}>
            {language === 'cs' ? 'Bezpečnost uživatelů' : 'User Safety'}
          </div>
        </div>
      </div>
    </DiscordDocument>
  )
}

export default DiscordViolentExtremismPolicyPage
